/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module avi/core
 */

import { isString } from 'underscore';
import { StateService } from '@uirouter/core';
import { AjsDependency } from 'ajs/utils/ajsDependency';

import {
    IAppRouterState,
    IAppState,
    IToStateParams,
} from 'ajs/js/services/appStates.types';

import { NavigationService } from 'ng/modules/navigation/services/navigation.service';
import { removeWildcard } from 'ng/modules/navigation/utils';
import { StateAccessService } from 'ng/modules/core/services/state-access/state-access.service';
import { getParentStateName } from 'ng/modules/core/utils/router-states.utils';
import { Auth } from '../auth';

/**
 * @description
 *      Service to find a closest allowed state when App/User is trying to load
 *      an invalid state (i.e., States without access/ Object detail pages from
 *      different tenants.)
 * @author Aravindh Nagarajan, alextsg
 */
export class StateManager extends AjsDependency {
    private $state: StateService;
    private auth: Auth;

    constructor() {
        super();

        this.$state = this.getAjsDependency_('$state');
        this.auth = this.getAjsDependency_('Auth');
    }

    /**
     * Returns true if state is an object detail page state.
     * All object detail states have `inTenantScope` set in their data.
     */
    private static isStateInTenantScope(state: IAppRouterState): boolean {
        const { data: stateData } = state;

        return Boolean(stateData?.inTenantScope);
    }

    /**
     * Returns true if toTenant and current tenant are same (or)
     * if toTenant is `All tenants` (*).
     * @param previousTenant Current Tenant name
     * @param newTenant Tenant name from toParams
     */
    private static newTenantIsSameOrAllTenants(previousTenant: string, newTenant: string): boolean {
        return newTenant === previousTenant || newTenant === '*';
    }

    /**
     * Return the first accessible and navigable state. Given a starting state, traverse up to the
     * first accessible and navigable parent, then find the deepest first accessible and navigable
     * child state.
     */
    public getClosestAllowedState(
        state: IAppRouterState | IAppRouterState['name'],
        toParams: IToStateParams,
        fromTenant = '',
    ): IAppRouterState {
        if (!isString(state)) {
            state = state.name;
        }

        let parentStateName = getParentStateName(state);

        while (!this.isAllowedState(parentStateName, null, fromTenant)) {
            parentStateName = getParentStateName(parentStateName);
        }

        if (!this.hasNavigableChildren(parentStateName, fromTenant)) {
            return this.getStateObject(parentStateName);
        }

        const allowedChildStateName = this.getFirstNavigableChildStateName(
            parentStateName,
            toParams,
            fromTenant,
        );

        return this.getStateObject(allowedChildStateName);
    }

    /**
     * Returns true when user can access a state without any issues. Checks whether
     * user has permission to load the page.
     *
     * We can check permissions for current tenant only(!).
     * If different tenantName is passed via 'toParams' we can't return `true` cause
     * we don't have permissions mapping for other tenant.
     * @param state
     * @param toParams
     * @param fromTenant - Passed by tenant-selector component only.
     * @todo return false if toParams.tenantName !== currentTenant
     *     cause we don't have rules mapping
     */
    public isAllowedState(
        state: IAppRouterState | IAppRouterState['name'],
        toParams: IToStateParams,
        fromTenant = '',
    ): boolean {
        if (isString(state)) {
            state = this.getStateObject(state);
        }

        const currentTenant = this.auth.getTenantName();

        return this.isNavigable(state.name, fromTenant) && (
            !StateManager.isStateInTenantScope(state) ||
            !fromTenant ||
            StateManager.newTenantIsSameOrAllTenants(fromTenant, currentTenant)
        );
    }

    /**
     * Return true if the state is accessible and navigable.
     */
    public isNavigable(stateName: string, fromTenant = ''): boolean {
        const stateObject = this.$state.get(stateName);
        const navigationService: NavigationService = this.getAjsDependency_('navigationService');
        const stateAccessService: StateAccessService = this.getAjsDependency_('stateAccessService');

        stateName = removeWildcard(stateName);

        const isAccessible = stateAccessService.isAccessible(stateName);

        if (!isAccessible) {
            return false;
        }

        // If the state is not abstract and the state is both defined as a navigable state and is
        // accessible, return true.
        if (!stateObject.abstract) {
            return navigationService.isDefined(stateName);
        }

        // If the state is abstract, then it's only navigable if any of its children are navigable.
        return this.hasNavigableChildren(stateName, fromTenant);
    }

    /**
     * Return true if the state has any children states that can be navigated to.
     */
    public hasNavigableChildren(stateName: string, fromTenant = ''): boolean {
        const navigationService: NavigationService = this.getAjsDependency_('navigationService');
        const childStates = navigationService.getChildrenStateNames(stateName);

        if (!childStates) {
            return false;
        }

        return childStates.some(childState => this.isAllowedState(childState, null, fromTenant));
    }

    /**
     * Returns the full name of the state.
     * @param abstractStateName - Partial name of the state to go to,
     * ex. authenticated.profile.
     */
    public getFullAllowedState(abstractStateName: string): string {
        const {
            $state,
        } = this;

        const abstractState = $state.get(abstractStateName) as IAppState;

        let targetStateName = '';

        const {
            abstract,
            defaultChild,
            name,
        } = abstractState;

        if (abstract && defaultChild) {
            targetStateName = `${name}.${defaultChild}`;
        } else {
            targetStateName = name;
        }

        if (this.isAllowedState(targetStateName, null)) {
            return targetStateName;
        }

        return this.getClosestAllowedState(targetStateName, null).name;
    }

    /**
     * Get the deepest first accessible and navigable child state given a parent state name.
     */
    private getFirstNavigableChildStateName(
        stateName: string,
        toParams: IToStateParams,
        fromTenant = '',
    ): string {
        const navigationService: NavigationService = this.getAjsDependency_('navigationService');

        let navigableStateName = stateName;

        while (navigationService.getChildrenStateNames(navigableStateName)) {
            const childStateNames = navigationService.getChildrenStateNames(navigableStateName);

            navigableStateName = childStateNames.find(childStateName => {
                return this.isAllowedState(childStateName, toParams, fromTenant);
            });
        }

        return navigableStateName;
    }

    /**
     * Returns state object for a state name.
     */
    private getStateObject(stateName: IAppRouterState['name']): IAppRouterState {
        const $state: StateService = this.getAjsDependency_('$state');

        return $state.get(stateName);
    }
}

StateManager.ajsDependencies = [
    '$state',
    'Auth',
    'stateAccessService',
    'navigationService',
];
